import { useGuideEnabled } from "../lib/guide-preference";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { QueueEntry, QueueSnapshot, SyncRunView } from "../../shared/global-queue";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { useViewerLogin } from "../hooks/useViewerLogin";
import { liveReviewPath } from "../../shared/pull-routes";
import { hostedDataStaleSince, loadHostedQueueSnapshot, loadHostedSyncRun, reconcileHostedQueue } from "../lib/hosted";
import { handleLocalNavigation } from "../lib/navigation";
import { bandFor, pullView, queueBands, repositoryShortName, type QueueBandKey } from "../lib/pull-view";
import { startVisibilityPolling } from "../lib/visibility-polling";
import { AppContextPath, AppShell } from "./AppShell";
import { PullBand, PullFlags, PullCard } from "./PullPresentation";
import { checksSignal, commentsSignal } from "../lib/pull-signals";

const gateLabels: Record<QueueEntry["chewGate"], string> = {
  guided: "Guided",
  unguided: "Unguided",
  "chewfile-required": "Chewfile required",
  "chewfile-stale": "Chewfile stale",
  "stack-blocked": "Stack blocked",
  unknown: "Gate unknown",
};

function relativeAge(value: string | null): string {
  if (!value) return "—";
  const elapsed = Date.now() - Date.parse(value);
  if (!Number.isFinite(elapsed) || elapsed < 0) return "—";
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function routeLabel(entry: QueueEntry): string {
  if (entry.routes.length === 0 || entry.routes.every((route) => route.type === "pool")) return "General pool";
  return entry.routes.map((route) => route.type === "pool" ? "General pool" : `@${route.target}`).join(", ");
}

function handlerLabel(entry: QueueEntry): string {
  if (entry.claim) return `@${entry.claim.reviewerLogin}`;
  if (entry.activeReviewer) return `@${entry.activeReviewer}`;
  return "Unclaimed";
}

function sourceTime(snapshot: QueueSnapshot): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(snapshot.generation.computedAt));
}

function terminalRun(run: SyncRunView): boolean {
  return ["succeeded", "partial", "failed"].includes(run.status);
}

function sameEntry(left: QueueEntry | undefined, right: QueueEntry): boolean {
  return left?.repository === right.repository && left.pullNumber === right.pullNumber;
}

export function GlobalQueuePage({ initialSnapshot, local = false }: { initialSnapshot: QueueSnapshot; local?: boolean }) {
  const guideEnabled = useGuideEnabled();
  usePageMetadata("Canonical queue · CHEW", "Every queued pull request in CHEW's canonical review order.");
  const viewerLogin = useViewerLogin();
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  const [search, setSearch] = useState("");
  const [settledOpen, setSettledOpen] = useState(false);
  const [repository, setRepository] = useState("all");

  const [selected, setSelected] = useState<QueueEntry>();
  const [run, setRun] = useState<SyncRunView>();
  const [reconcileError, setReconcileError] = useState<string>();
  const [reconciling, setReconciling] = useState(false);
  const [staleSince, setStaleSince] = useState<string>();

  useEffect(() => {
    if (local) return;
    return startVisibilityPolling(async () => {
      const next = await loadHostedQueueSnapshot({ refresh: true });
      if (!next?.entries) return;
      setSnapshot(next);
      setStaleSince(hostedDataStaleSince("queue"));
      // Keep the open detail pane if its entry momentarily drops out of a
      // partial snapshot; closing it under the reader mid-poll is worse than
      // showing slightly stale details.
      setSelected((current) => current ? next.entries.find((entry) => sameEntry(current, entry)) ?? current : undefined);
    }, { runImmediately: false });
  }, [local]);

  useEffect(() => {
    if (!selected) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(undefined);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [selected]);

  const repositories = useMemo(
    () => [...new Set(snapshot.entries.map((entry) => entry.repository))].sort(),
    [snapshot.entries],
  );
  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return snapshot.entries.filter((entry) => {
      if (repository !== "all" && entry.repository !== repository) return false;
      if (needle && ![
        entry.title,
        entry.repository,
        String(entry.pullNumber),
        entry.authorLogin,
        routeLabel(entry),
        handlerLabel(entry),
      ].some((value) => value.toLocaleLowerCase().includes(needle))) return false;
      return true;
    });
  }, [repository, search, snapshot.entries]);

  const startReconciliation = async () => {
    if (local) return;
    setReconciling(true);
    setReconcileError(undefined);
    try {
      const accepted = await reconcileHostedQueue();
      let latest: SyncRunView;
      do {
        await new Promise((resolve) => window.setTimeout(resolve, 900));
        latest = await loadHostedSyncRun(accepted.runId);
        setRun(latest);
      } while (!terminalRun(latest));
      const next = await loadHostedQueueSnapshot({ refresh: true });
      setSnapshot(next);
      setSelected((current) => current ? next.entries.find((entry) => sameEntry(current, entry)) ?? current : undefined);
    } catch (reason) {
      setReconcileError(reason instanceof Error ? reason.message : "CHEW could not start reconciliation.");
    } finally {
      setReconciling(false);
    }
  };

  const banded = useMemo(() => {
    const groups = new Map<QueueBandKey, QueueEntry[]>();
    for (const entry of [...filtered].sort((left, right) => left.position - right.position)) {
      const key = bandFor(pullView(entry, viewerLogin), entry);
      groups.set(key, [...(groups.get(key) ?? []), entry]);
    }
    return groups;
  }, [filtered, viewerLogin]);

  const degraded = snapshot.generation.stale || snapshot.repositories.some((item) => item.stale);

  return (
    <AppShell active="operations" center={<AppContextPath items={[{ label: "Operations" }, { label: "Canonical queue" }]} />} queueSnapshot={snapshot}>
      <main className="global-queue-shell">
        <div className="global-queue-content">
          <header className="queue-page-head">
            <div>
              <span>Operational routing</span>
              <h1>Canonical queue</h1>
              <p>Every open pull request, grouped by what can be done about it. Canonical position is kept inside each group.</p>
            </div>
            <div className={`queue-projection-state${degraded ? " is-degraded" : ""}`}>
              {degraded ? <AlertTriangle aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}
              <span><strong>{degraded ? "Sync delayed" : "Up to date"}</strong><small>{sourceTime(snapshot)} · {snapshot.generation.id.slice(-8)}</small></span>
              {!local && <button disabled={reconciling} onClick={() => { void startReconciliation(); }} type="button"><RefreshCw aria-hidden="true" className={reconciling ? "is-spinning" : undefined} />{reconciling ? "Reconciling" : "Reconcile"}</button>}
            </div>
          </header>

          {(degraded || run || reconcileError || staleSince) && (
            <section className="queue-run-strip" aria-live="polite">
              {staleSince && <p><AlertTriangle aria-hidden="true" />CHEW cannot reach the server. Showing the queue saved {new Date(staleSince).toLocaleString()} — it keeps retrying.</p>}
              {run && <p><strong>Resync {run.id.slice(-8)}</strong> · {run.status} · {run.steps.filter((step) => step.status === "succeeded").length}/{run.steps.length} steps</p>}
              {degraded && snapshot.warnings.map((warning) => <p key={warning}><AlertTriangle aria-hidden="true" />{warning}</p>)}
              {reconcileError && <p className="is-error"><AlertTriangle aria-hidden="true" />{reconcileError}</p>}
            </section>
          )}

          <div className="queue-controls" role="search">
            <label className="queue-search">
              <Search aria-hidden="true" />
              <input aria-label="Search the queue" onChange={(event) => setSearch(event.target.value)} placeholder="Title, number, person, repository" type="search" value={search} />
            </label>
            <select aria-label="Repository" onChange={(event) => setRepository(event.target.value)} value={repository}>
              <option value="all">All repositories</option>
              {repositories.map((value) => <option key={value}>{value}</option>)}
            </select>
            <span className="queue-controls-tally">{filtered.length} of {snapshot.entries.length}</span>
          </div>

          {filtered.length > 0 ? queueBands.map((band) => {
            const entries = banded.get(band.key) ?? [];
            if (entries.length === 0) return null;
            const collapsed = band.key === "settled" && !settledOpen;
            return (
              <PullBand blurb={band.blurb} count={entries.length} key={band.key} title={band.title}>
                {collapsed ? (
                  <button className="queue-band-expand" onClick={() => setSettledOpen(true)} type="button">
                    Show {entries.length} settled {entries.length === 1 ? "pull request" : "pull requests"}
                  </button>
                ) : entries.map((entry) => (
                  <PullCard
                    actions={
                      <button
                        aria-label={`Inspect global position ${entry.position}, ${entry.title}`}
                        className="pv-act pv-act-icon"
                        onClick={() => setSelected(entry)}
                        type="button"
                      ><ChevronRight aria-hidden="true" /></button>
                    }
                    key={`${entry.repository}#${entry.pullNumber}`}
                    rank={entry.position}
                    selected={sameEntry(selected, entry)}
                    view={pullView(entry, viewerLogin)}
                  />
                ))}
              </PullBand>
            );
          }) : (
            <div className="queue-empty"><strong>Nothing matches that search.</strong><p>Clear it to see the whole queue again.</p></div>
          )}

          <details className="reviewer-coverage">
            <summary><span><strong>Reviewer coverage</strong><small>Why backlog may not become opportunity</small></span><em>{snapshot.reviewerCoverage.length} reviewers</em><ChevronDown aria-hidden="true" /></summary>
            <p>Aggregates are restricted to repositories you can see.</p>
            <div className="reviewer-coverage-grid">
              {snapshot.reviewerCoverage.map((reviewer) => (
                <article key={reviewer.reviewer}>
                  <header><strong>@{reviewer.reviewer}</strong><span>{reviewer.availableOpportunities} available</span></header>
                  <dl><div><dt>Assigned</dt><dd>{reviewer.assignments}</dd></div><div><dt>Active</dt><dd>{reviewer.activeWork}</dd></div></dl>
                  {reviewer.availableOpportunities === 0 && <ul>{Object.entries(reviewer.zeroOpportunityReasons).sort((left, right) => right[1] - left[1]).slice(0, 5).map(([reason, count]) => <li key={reason}><span>{reason}</span><strong>{count}</strong></li>)}</ul>}
                </article>
              ))}
            </div>
          </details>
        </div>

        {selected && <>
          <button aria-label="Close queue inspector" className="queue-detail-backdrop" onClick={() => setSelected(undefined)} type="button" />
          <aside aria-label={`Queue detail for ${selected.title}`} className="queue-detail-panel">
            <header><div><span>Global position {selected.position}</span><h2>{selected.title}</h2><p>{repositoryShortName(selected.repository)} #{selected.pullNumber} · @{selected.authorLogin}</p></div><button aria-label="Close queue detail" onClick={() => setSelected(undefined)} type="button"><X aria-hidden="true" /></button></header>
            <div className="queue-detail-body">
              {(() => { const view = pullView(selected, viewerLogin); return (<>
                {view.exceptions.length > 0 && <p className="queue-detail-flags"><PullFlags exceptions={view.exceptions} /></p>}
                <section className="queue-detail-summary"><span>Routing summary</span><dl>
                  <div><dt>Status</dt><dd>{view.state.label}</dd></div>
                  <div><dt>Route</dt><dd>{routeLabel(selected)}</dd></div>
                  <div><dt>Held by</dt><dd>{view.holder ? view.holder.detail : "Nobody"}</dd></div>
                  <div><dt>You</dt><dd>{selected.forMe.detail}</dd></div>
                  <div><dt>Waiting</dt><dd>{relativeAge(selected.waitSince)}</dd></div>
                </dl></section>
              </>); })()}
              <section className="queue-detail-facts"><span>Pull request attention</span><dl>
                <div><dt>Checks</dt><dd><em className={`queue-status${selected.attention.ci?.state === "failure" ? " status-error" : ""}`}>{selected.attention.ci ? checksSignal(selected.attention.ci).full : "Not synchronized"}</em></dd></div>
                <div><dt>Comments</dt><dd>{selected.attention.conversation ? commentsSignal(selected.attention.conversation).full : "Not synchronized"}</dd></div>
                <div><dt>Author</dt><dd>@{selected.authorLogin}</dd></div>
                <div><dt>Reviewer</dt><dd>{handlerLabel(selected) === "Unclaimed" ? routeLabel(selected) : handlerLabel(selected)}</dd></div>
              </dl>{selected.attention.conversation?.latest && <p><a href={selected.attention.conversation.latest.url} rel="noreferrer" target="_blank">Latest from @{selected.attention.conversation.latest.author}: {selected.attention.conversation.latest.body || "Open comment"}</a></p>}</section>
              <section><span>Eligibility ladder</span><ol>
                <li><strong>1 · GitHub lifecycle</strong><em>{selected.lifecycle}</em></li>
                <li><strong>2 · Review need</strong><em>{selected.reviewNeed}</em></li>
                <li><strong>3 · CHEW gate</strong><em>{gateLabels[selected.chewGate]}</em></li>
                <li><strong>4 · Routing</strong><em>{selected.routing}</em></li>
                <li><strong>5 · Coordination</strong><em>{selected.coordination}</em></li>
              </ol></section>
              <section><span>Personal eligibility</span><code>{selected.forMe.reasonCode}</code><p>{selected.forMe.detail}</p></section>
              <section><span>Canonical status</span><code>{selected.reasonCode}</code><p>{selected.reasonDetail}</p></section>
              <section className="queue-detail-facts"><span>Gate, stack &amp; projection</span><dl>
                {guideEnabled && <div><dt>Guide gate</dt><dd>{gateLabels[selected.chewGate]}</dd></div>}
                <div><dt>Stack</dt><dd>{selected.stackState}</dd></div>
                <div><dt>Head</dt><dd><code>{selected.headSha.slice(0, 12)}</code></dd></div>
                <div><dt>Source</dt><dd>{selected.sourceCompleteness}</dd></div>
                <div><dt>Observed</dt><dd>{new Date(selected.observedAt).toLocaleString()}</dd></div>
                <div><dt>Ready since</dt><dd>{selected.waitSince ? new Date(selected.waitSince).toLocaleString() : "Not ready"}</dd></div>
                <div><dt>Changed</dt><dd>{selected.stats.additions ?? "?"}+ / {selected.stats.deletions ?? "?"}−</dd></div>
                <div><dt>Estimate</dt><dd>{selected.estimatedMinutes ? `${selected.estimatedMinutes} min` : "Unknown"}</dd></div>
              </dl></section>
            </div>
            <footer>
              <a
                href={liveReviewPath(selected.repository, selected.pullNumber)}
                onClick={(event) => handleLocalNavigation(event, liveReviewPath(selected.repository, selected.pullNumber))}
              >Open in CHEW</a>
              <a href={selected.url ?? `https://github.com/${selected.repository}/pull/${selected.pullNumber}`} rel="noreferrer" target="_blank">Open on GitHub <ExternalLink aria-hidden="true" /></a>
            </footer>
          </aside>
        </>}
      </main>
    </AppShell>
  );
}
